import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Platform,
    TouchableOpacity,
    View,
    useWindowDimensions,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { FlingGesture, PinchGesture } from 'react-native-gesture-handler';
import { OrientationType } from 'react-native-orientation-locker';
import Animated from 'react-native-reanimated';
import { useDispatch, useSelector } from 'react-redux';

import debounce from 'lodash.debounce';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { selectWarnOnMobileData } from 'store/connection/selectors';
import { setFocusToNow } from 'store/epg/actions';
import { PlayerTrack, PlayerTracks } from 'store/player/reducer.types';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import { VideoItem } from 'services/data-manager/types';

import { ERROR_CODE_P008 } from 'utils/error/errorCodes';
import isFunction from 'utils/function';
import useDebouncedSafeAreaInsets from 'utils/hooks/useDebouncedSafeAreaInsets';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import { UseRecordingsHookReturnType } from 'utils/hooks/useRecordings';
import { isEmpty } from 'utils/object';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import {
    heightPercentageToDP,
    isPhone,
    isTablet,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import EmbeddedErrorBackground from 'screens/PlayerModal/components/EmbeddedErrorBackground';
import Error from 'screens/PlayerModal/components/Error';
import useChannelsZapping, {
    DEFAULT_ZAPPING_CONFIG,
} from 'screens/PlayerModal/hooks/useChannelsZapping';
import useFrameFitting, {
    DEFAULT_FRAME_FITTING_CONFIG,
} from 'screens/PlayerModal/hooks/useFrameFitting';
import { PlayerControlsAccessLevel } from 'screens/PlayerModal/hooks/useTrickModeControl';
import { PlayerError } from 'screens/PlayerModal/types';

import VolumeControl from 'components/PlayerOverlay/VolumeControl';
import SelectAudioAndSubtitles from 'components/SelectAudioAndSubtitles';
import SelectRecordingActions from 'components/SelectRecordingActions';
import useRecordingActionsDialog from 'components/SelectRecordingActions/hooks/useRecordingActionsDialog';

import BottomOverlaySection from './BottomOverlaySection';
import BrightnessControl from './BrightnessControl';
import GestureHandlerComponent from './GestureHandlerComponent';
import MiddleOverlaySection from './MiddleOverlaySection';
import PlayerOverlayAirPlay from './PlayerOverlayAirPlay';
import PlayerOverlayLoading from './PlayerOverlayLoading';
import TopMinimizedOverlaySection from './TopMinimizedOverlaySection';
import TopOverlaySection from './TopOverlaySection';
import ZappingChannelLogo from './ZappingChannelLogo';
import useShowHideAnimation from './hooks/useShowHideAnimation';
import styles from './styles';

type Props = {
    playerError: PlayerError;
    videoItem?: VideoItem;
    seeking: boolean;
    timeshiftModeOn: boolean;
    timeshiftPosition: number | null;
    timeshiftDuration: number | undefined;
    startoverAllowed: boolean;
    startoverButtonVisible: boolean;
    jumpToLiveAllowed: boolean;
    skipBackwardDisabled: boolean;
    skipForwardDisabled: boolean;
    pauseAccessLevel: PlayerControlsAccessLevel;
    rewindAccessLevel: PlayerControlsAccessLevel;
    fastforwardAccessLevel: PlayerControlsAccessLevel;
    isLive: boolean;
    isStartover: boolean;
    isCatchup: boolean;
    isVod: boolean;
    isNpvr: boolean;
    readyToPlay: boolean;
    isPaused: boolean;
    fullscreen: boolean;
    embedded?: boolean;
    noTitleBlock?: boolean;
    position: number;
    duration: number;
    livePosition?: number;
    airTimeStart?: number;
    airTimeEnd?: number;
    primaryColor: string;
    playerTracks?: PlayerTracks;
    selectedAudio?: PlayerTrack;
    selectedSubtitles?: PlayerTrack;
    subtitlesDialogVisible?: boolean;
    isSOTSPauseDisabled?: boolean;
    subtitleButtonPosition?:
        | { x: number; y: number; width: number; height: number }
        | undefined;
    volume: number;
    onSubtitlesButtonPress?(layout: {
        x: number;
        y: number;
        width: number;
        height: number;
    }): void;
    onCloseSubtitlesDialog?(): void;
    onSubtitlesModalClosed?(): void;
    onPlayPausePress(): void;
    onSlidingStart(): void;
    onSkipForward(): void;
    onSkipBackward(): void;
    onCloseModal(): void;
    onSlidingComplete(value: number): void;
    onSlidingCompleteSilently?(value: number): void;
    onFullscreenPress(): void;
    onMinimizePress(): void;
    onPictureInPicturePress?(): void;
    onSlidingValueChanged?(progress: number): void;
    onChangeVolume(volume: number): void;
    onOpenDetails?(): void;
    onJumpToLive?(): void;
    onStartover(): void;
    onSelectAudio?(trackId: PlayerTrack): void;
    onSelectSubtitles?(trackId: PlayerTrack): void;
    orientation?: OrientationType;
    timeshiftIndicatorEnabled?: boolean;
    routeKey?: string;
    recordingActionsDialogState?: ReturnType<typeof useRecordingActionsDialog>;
    recordingItem?: UseRecordingsHookReturnType['recordingItem'];
    zapping?: ReturnType<typeof useChannelsZapping>;
    screenReaderEnabled: boolean;
    frameFitting?: ReturnType<typeof useFrameFitting>;
    muted?: boolean;
    onToggleMute?(): void;
    playerWaiting: boolean;
    isBingeWatchingVisible?: boolean;
};

const PLAYER_OVERLAY_ELEMENT_ID = 'player-overlay';
const {
    ACCESSIBILITY: {
        PLAYER: { ACCESSIBILITY_PLAYER_OVERLAY_CONTROLS },
    },
} = LANGUAGE_KEYS;

function playerTracksKeyExtractor(track: PlayerTrack) {
    return `${track.id}-${track.language}`;
}

const PlayerOverlay = ({
    playerError,
    videoItem,
    timeshiftModeOn,
    timeshiftPosition,
    timeshiftDuration,
    startoverAllowed,
    startoverButtonVisible,
    jumpToLiveAllowed,
    position = 0,
    duration = 0,
    livePosition,
    readyToPlay = false,
    isPaused = true,
    embedded = false,
    noTitleBlock = false,
    isSOTSPauseDisabled,
    skipBackwardDisabled,
    skipForwardDisabled,
    pauseAccessLevel,
    rewindAccessLevel,
    fastforwardAccessLevel,
    volume,
    onPlayPausePress,
    onSlidingStart,
    onSkipForward,
    onSkipBackward,
    onCloseModal,
    onSlidingComplete,
    onSlidingCompleteSilently,
    primaryColor,
    airTimeStart,
    airTimeEnd,
    isLive,
    isCatchup,
    isStartover,
    isVod,
    isNpvr,
    fullscreen,
    seeking,
    playerTracks,
    selectedAudio,
    selectedSubtitles,
    subtitlesDialogVisible,
    subtitleButtonPosition,
    onSubtitlesButtonPress,
    onCloseSubtitlesDialog,
    onSubtitlesModalClosed,
    onSlidingValueChanged,
    onOpenDetails,
    onJumpToLive,
    onStartover,
    onFullscreenPress,
    onMinimizePress,
    onPictureInPicturePress,
    onSelectAudio,
    onSelectSubtitles,
    onChangeVolume,
    orientation,
    timeshiftIndicatorEnabled,
    routeKey = '',
    recordingActionsDialogState,
    recordingItem,
    zapping = DEFAULT_ZAPPING_CONFIG,
    screenReaderEnabled,
    frameFitting = DEFAULT_FRAME_FITTING_CONFIG,
    muted,
    onToggleMute,
    playerWaiting,
    isBingeWatchingVisible,
}: Props) => {
    const { t } = useTranslation();

    // TODO: Disabled due to REAC-2172 issue(s) related to DRM configuration.
    // const isAirPlayConnected = useAirplayConnectivity();
    const isAirPlayConnected = false;
    const { channelsZapping, zappedChannelId, flingGestures, zappingEnabled } =
        zapping;
    const {
        enabled: frameFittingEnabled,
        pinchGestures,
        frameFittingOn,
        onToggleFrameFitting,
    } = frameFitting;

    const dispatch = useDispatch();
    const insets = useDebouncedSafeAreaInsets();
    const shouldWarnOnMobileData = useSelector(selectWarnOnMobileData);

    const errorExists = !isEmpty(playerError);
    const isPhoneEpgPlayer = embedded && !fullscreen && isPhone;
    const isTabletEpgPlayer = embedded && !fullscreen && isTablet;
    const isWebEpgPlayer = embedded && !fullscreen && isWeb;

    const [webSubtitlesSize, setWebSubtitlesSize] = useState<string | null>();

    const overlayIsLoading =
        isEmpty(playerError) && (!readyToPlay || seeking || playerWaiting);
    const showRecordingActionsButton =
        recordingActionsDialogState?.showRecordingActionsButton && !errorExists;

    const {
        overlayVisible,
        animatedStyle,
        backgroundAnimatedStyle,
        handleOverlayPress,
        handleProlongOverlayVisibility,
        handleShowOverlayConstantly,
        handleWebMouseMove,
    } = useShowHideAnimation({
        screenReaderEnabled,
        subtitlesDialogVisible,
        errorExists,
        readyToPlay,
        recordingActionsDialogVisible:
            recordingActionsDialogState?.recordingActionsDialogVisible,
        videoItem,
        isAirPlayConnected,
        isBingeWatchingVisible,
    });

    const { width } = useWindowDimensions();
    const subDialogIsVisible = !!(
        subtitlesDialogVisible ||
        recordingActionsDialogState?.recordingActionsDialogVisible
    );

    const overlayIndents = useMemo(() => {
        const paddingHorizontal =
            isTabletEpgPlayer || isPhoneEpgPlayer
                ? 0
                : Platform.select({
                      web: widthPercentageToDP(72),
                      default: Math.max(
                          insets.left,
                          insets.right,
                          widthPercentageToDP(44, 44),
                      ),
                  });
        const paddingTop =
            isTabletEpgPlayer || isPhoneEpgPlayer || isWebEpgPlayer
                ? Platform.select({
                      web: heightPercentageToDP(16),
                      default: 0,
                  })
                : Platform.select({ web: 20, default: 32 });

        return {
            paddingTop,
            paddingHorizontal,
        };
    }, [insets, isPhoneEpgPlayer, isTabletEpgPlayer, isWebEpgPlayer]);
    const isUnsubscribedChannel = useMemo(
        () => playerError?.code === ERROR_CODE_P008,
        [playerError],
    );

    const profileParentalRating = useProfileParentalRating();
    const isContentLocked = useMemo(
        () =>
            needsToLockContent(
                profileParentalRating,
                videoItem?.parentalRating,
            ),
        [profileParentalRating, videoItem],
    );

    const gestures = useMemo(() => {
        let _gestures: (FlingGesture | PinchGesture)[] = [];

        if (flingGestures?.length) {
            _gestures = [..._gestures, ...flingGestures];
        }

        if (pinchGestures?.length) {
            _gestures = [..._gestures, ...pinchGestures];
        }

        return _gestures;
    }, [flingGestures, pinchGestures]);

    const handleClosePress = useCallback(() => {
        if (embedded) {
            onCloseModal();
        } else {
            handleProlongOverlayVisibility(() => {
                onCloseModal();
            });
        }
    }, [embedded, handleProlongOverlayVisibility, onCloseModal]);

    const handleOpenDetailsPress = useCallback(() => {
        if (isFunction(onOpenDetails)) {
            onOpenDetails();
        }
    }, [embedded, onOpenDetails]);

    const loadingCirclePaddingBottom = useMemo(() => {
        if (
            isPhone &&
            orientation === OrientationType[OrientationType.PORTRAIT] &&
            fullscreen &&
            !(isTabletEpgPlayer || isPhoneEpgPlayer)
        ) {
            return insets.left || insets.right
                ? widthPercentageToDP(20)
                : widthPercentageToDP(44);
        }

        if (isPhone && fullscreen && (!isTabletEpgPlayer || isPhoneEpgPlayer)) {
            return heightPercentageToDP(32);
        }

        return 0;
    }, [orientation, fullscreen, insets, isTabletEpgPlayer, isPhoneEpgPlayer]);

    const handleWebDoubleClick = useCallback(() => {
        if (fullscreen) {
            onMinimizePress();
        } else {
            onFullscreenPress();
        }
    }, [fullscreen, onMinimizePress, onFullscreenPress]);

    const handleSkipBackward = useCallback(() => {
        handleProlongOverlayVisibility(onSkipBackward);
    }, [handleProlongOverlayVisibility, onSkipBackward]);

    const handlePlayPausePress = useCallback(() => {
        handleProlongOverlayVisibility(onPlayPausePress);
    }, [handleProlongOverlayVisibility, onPlayPausePress]);

    const handleSkipForward = useCallback(() => {
        handleProlongOverlayVisibility(onSkipForward);
    }, [handleProlongOverlayVisibility, onSkipForward]);

    const handleSlidingStart = useCallback(() => {
        handleShowOverlayConstantly();
        onSlidingStart();
    }, [handleShowOverlayConstantly, onSlidingStart]);

    const handleSlidingComplete = useCallback(
        (value: number) => {
            handleProlongOverlayVisibility(() => {
                onSlidingComplete(value);
            });
        },
        [handleProlongOverlayVisibility, onSlidingComplete],
    );

    const handleSlidingCompleteSilently = useCallback(
        (value: number) => {
            handleProlongOverlayVisibility(() => {
                onSlidingCompleteSilently?.(value);
            });
        },
        [handleProlongOverlayVisibility, onSlidingCompleteSilently],
    );

    const handleCloseSubtitlesDialog = useCallback(() => {
        if (isFunction(onCloseSubtitlesDialog)) {
            onCloseSubtitlesDialog();
        }
    }, [onCloseSubtitlesDialog]);

    const handleRecordActionButton = useCallback(
        (layout: { x: number; y: number; width: number; height: number }) => {
            handleProlongOverlayVisibility(() => {
                recordingActionsDialogState?.handleRecordingActionsButtonPress(
                    layout,
                );
            });
        },
        [
            handleProlongOverlayVisibility,
            recordingActionsDialogState,
            isLive,
            videoItem,
        ],
    );

    const handleStartoverPress = useCallback(() => {
        dispatch(startSentryTransaction(SENTRY_TRANSACTIONS.RestartPlayback));
        handleProlongOverlayVisibility(onStartover);
    }, [onStartover]);

    const handleJumpToLivePress = useCallback(() => {
        dispatch(startSentryTransaction(SENTRY_TRANSACTIONS.JumpToLive));
        if (isFunction(onJumpToLive)) {
            handleProlongOverlayVisibility(onJumpToLive);
        }
    }, [onJumpToLive]);

    const handleFullscreenPress = useCallback(() => {
        handleProlongOverlayVisibility(onFullscreenPress);
        dispatch(setFocusToNow(false));
    }, [onFullscreenPress]);

    const handleMinimizePress = useCallback(() => {
        handleProlongOverlayVisibility(onMinimizePress);
    }, [onMinimizePress]);

    const increaseSubtitleSize = useCallback((size: string) => {
        const containers = [
            // @ts-ignore
            ...document.getElementsByClassName('shaka-text-container'),
        ];

        if (containers.length) {
            containers.forEach((container: unknown) => {
                // eslint-disable-next-line no-param-reassign
                (container as HTMLElement).style.fontSize = size;
            });
        }
    }, []);

    const calculateWebSubtitleSize = useCallback(
        debounce(async () => {
            const size = fullscreen ? 2.5 : 1.5;
            const minSize = fullscreen ? 40 : 25;
            const calculatedSize =
                (width * size) / 100 >= minSize ? `${size}vw` : `${minSize}px`;

            if (calculatedSize !== webSubtitlesSize) {
                setWebSubtitlesSize(calculatedSize);
                increaseSubtitleSize(calculatedSize);
            }
        }, 100),
        [fullscreen, increaseSubtitleSize, width],
    );

    useEffect(() => {
        if (
            isWeb &&
            selectedSubtitles &&
            selectedSubtitles.title !== LANGUAGE_KEYS.SCREEN_SUBTITLE_OFF
        ) {
            calculateWebSubtitleSize();
        }
    }, [selectedSubtitles, calculateWebSubtitleSize]);

    useEffect(() => {
        if (isWeb) {
            document
                .getElementById(`${PLAYER_OVERLAY_ELEMENT_ID}${routeKey}`)
                ?.addEventListener('dblclick', handleWebDoubleClick);
        }

        return () => {
            if (isWeb) {
                document
                    .getElementById(`${PLAYER_OVERLAY_ELEMENT_ID}${routeKey}`)
                    ?.removeEventListener('dblclick', handleWebDoubleClick);
            }
        };
    }, [handleWebDoubleClick, routeKey]);

    if (shouldWarnOnMobileData) {
        return null;
    }

    return (
        <GestureHandlerComponent gestures={gestures}>
            <Animated.View
                style={[
                    styles.safeArea,
                    backgroundAnimatedStyle,
                    !isTablet && fullscreen && { height: '100%' },
                ]}
            >
                <TouchableOpacity
                    id={`${PLAYER_OVERLAY_ELEMENT_ID}${routeKey}`}
                    accessible={
                        !overlayVisible &&
                        !overlayIsLoading &&
                        !isAirPlayConnected
                    }
                    accessibilityLabel={t(
                        ACCESSIBILITY_PLAYER_OVERLAY_CONTROLS,
                    )}
                    accessibilityLiveRegion="none"
                    importantForAccessibility={
                        // eslint-disable-next-line no-nested-ternary
                        subDialogIsVisible
                            ? 'no-hide-descendants'
                            : overlayVisible
                            ? 'no'
                            : 'auto'
                    }
                    accessibilityElementsHidden={subDialogIsVisible}
                    activeOpacity={1}
                    style={[
                        styles.container,
                        overlayIndents,
                        // @ts-ignore
                        Platform.select({
                            web: {
                                cursor: overlayVisible ? 'pointer' : 'none',
                            },
                            default: undefined,
                        }),
                    ]}
                    disabled={
                        errorExists || channelsZapping || isAirPlayConnected
                    }
                    onPress={handleOverlayPress}
                    onMouseMove={handleWebMouseMove}
                >
                    <View style={styles.container} accessible={false}>
                        <Animated.View
                            style={[styles.container, animatedStyle]}
                            accessible={false}
                        >
                            {errorExists && embedded && !fullscreen && (
                                <EmbeddedErrorBackground />
                            )}
                            {isPhoneEpgPlayer ? (
                                <TopMinimizedOverlaySection
                                    onFullscreenPress={onFullscreenPress}
                                    onPictureInPicturePress={
                                        onPictureInPicturePress
                                    }
                                    showOverlay={
                                        (readyToPlay || errorExists) &&
                                        overlayVisible
                                    }
                                />
                            ) : (
                                <TopOverlaySection
                                    videoItem={videoItem}
                                    duration={duration}
                                    jumpToLiveAllowed={jumpToLiveAllowed}
                                    showOverlay={
                                        (readyToPlay || errorExists) &&
                                        overlayVisible
                                    }
                                    startoverButtonVisible={
                                        startoverButtonVisible
                                    }
                                    showRecordingActionsButton={
                                        showRecordingActionsButton
                                    }
                                    isCatchup={isCatchup}
                                    isVod={isVod}
                                    fullscreen={fullscreen}
                                    embedded={embedded}
                                    playerTracks={playerTracks}
                                    noTitleBlock={noTitleBlock}
                                    onOpenDetails={handleOpenDetailsPress}
                                    onJumpToLive={handleJumpToLivePress}
                                    onStartover={handleStartoverPress}
                                    onFullscreenPress={handleFullscreenPress}
                                    onMinimizePress={handleMinimizePress}
                                    onPictureInPicturePress={
                                        onPictureInPicturePress
                                    }
                                    onSubtitlePress={onSubtitlesButtonPress}
                                    onRecordingActionPress={
                                        handleRecordActionButton
                                    }
                                    onCloseModal={handleClosePress}
                                    recordingItem={recordingItem}
                                    screenReaderEnabled={screenReaderEnabled}
                                    frameFittingEnabled={frameFittingEnabled}
                                    frameFittingOn={frameFittingOn}
                                    onToggleFrameFitting={onToggleFrameFitting}
                                    muted={muted}
                                    onToggleMute={onToggleMute}
                                />
                            )}
                            {errorExists ? (
                                <Error
                                    error={playerError}
                                    fullscreen={Platform.select({
                                        web: fullscreen || !embedded,
                                        default: fullscreen,
                                    })}
                                    boldTitle={isContentLocked}
                                />
                            ) : (
                                <MiddleOverlaySection
                                    seeking={seeking}
                                    skipBackwardDisabled={skipBackwardDisabled}
                                    skipForwardDisabled={skipForwardDisabled}
                                    pauseAccessLevel={pauseAccessLevel}
                                    rewindAccessLevel={rewindAccessLevel}
                                    fastforwardAccessLevel={
                                        fastforwardAccessLevel
                                    }
                                    showOverlay={
                                        readyToPlay &&
                                        !playerWaiting &&
                                        overlayVisible
                                    }
                                    isPaused={isPaused}
                                    onSkipBackward={handleSkipBackward}
                                    onPlayPausePress={handlePlayPausePress}
                                    onSkipForward={handleSkipForward}
                                    marginBottom={loadingCirclePaddingBottom}
                                />
                            )}
                            <BottomOverlaySection
                                embedded={embedded}
                                fullscreen={fullscreen}
                                playerError={playerError}
                                isLive={isLive}
                                isSOTSPauseDisabled={isSOTSPauseDisabled}
                                isVod={isVod}
                                isCatchup={isCatchup}
                                isStartover={isStartover}
                                isNpvr={isNpvr}
                                timeshiftModeOn={timeshiftModeOn}
                                timeshiftPosition={timeshiftPosition}
                                timeshiftDuration={timeshiftDuration}
                                startoverAllowed={startoverAllowed}
                                showOverlay={
                                    readyToPlay &&
                                    overlayVisible &&
                                    !isUnsubscribedChannel &&
                                    !errorExists
                                }
                                position={position}
                                duration={duration}
                                livePosition={livePosition}
                                airTimeStart={airTimeStart}
                                airTimeEnd={airTimeEnd}
                                progressColor={primaryColor}
                                horizontalIndent={
                                    overlayIndents.paddingHorizontal
                                }
                                onSlidingStart={handleSlidingStart}
                                onSlidingValueChanged={onSlidingValueChanged}
                                onSlidingComplete={handleSlidingComplete}
                                onSlidingCompleteSilently={
                                    handleSlidingCompleteSilently
                                }
                                timeshiftIndicatorEnabled={
                                    timeshiftIndicatorEnabled
                                }
                                skipBackwardDisabled={skipBackwardDisabled}
                                skipForwardDisabled={skipForwardDisabled}
                                rewindAccessLevel={rewindAccessLevel}
                                fastforwardAccessLevel={fastforwardAccessLevel}
                            />
                            {readyToPlay &&
                                overlayVisible &&
                                !isUnsubscribedChannel &&
                                !errorExists &&
                                fullscreen && (
                                    <BrightnessControl
                                        onProlongOverlayVisiblity={
                                            handleProlongOverlayVisibility
                                        }
                                    />
                                )}
                            {readyToPlay &&
                                overlayVisible &&
                                !isUnsubscribedChannel &&
                                !errorExists && (
                                    <VolumeControl
                                        volume={volume}
                                        onChangeVolume={onChangeVolume}
                                        onProlongOverlayVisiblity={
                                            handleProlongOverlayVisibility
                                        }
                                    />
                                )}
                        </Animated.View>
                        {overlayIsLoading && !isAirPlayConnected && (
                            <PlayerOverlayLoading
                                loadingCirclePaddingBottom={
                                    loadingCirclePaddingBottom
                                }
                                color={primaryColor}
                                onClose={handleClosePress}
                                seeking={seeking}
                                embedded={embedded}
                                waiting={playerWaiting}
                                routeKey={routeKey}
                            />
                        )}
                        {isAirPlayConnected && !errorExists && (
                            <PlayerOverlayAirPlay
                                onMinimizeEmbeddedPlayer={onCloseModal}
                                onClose={handleClosePress}
                                embedded={embedded}
                                fullscreen={fullscreen}
                            />
                        )}
                    </View>
                </TouchableOpacity>
                {zappingEnabled && channelsZapping && !!zappedChannelId && (
                    <ZappingChannelLogo channelId={zappedChannelId} />
                )}
                {subtitlesDialogVisible != null &&
                    playerTracks != null &&
                    isFunction(onSelectAudio) &&
                    isFunction(onSelectSubtitles) && (
                        <SelectAudioAndSubtitles
                            keyExtractor={playerTracksKeyExtractor}
                            isVisible={subtitlesDialogVisible}
                            selectedAudioTrack={selectedAudio}
                            selectedTextTrack={selectedSubtitles}
                            audioTracks={playerTracks.audioTracks}
                            textTracks={playerTracks.textTracks}
                            subtitleButtonPosition={subtitleButtonPosition}
                            onAudioTrackPress={onSelectAudio}
                            onTextTrackPress={onSelectSubtitles}
                            onClose={handleCloseSubtitlesDialog}
                            onModalClosed={onSubtitlesModalClosed}
                        />
                    )}
                {!!recordingActionsDialogState && (
                    <SelectRecordingActions
                        isVisible={
                            recordingActionsDialogState.recordingActionsDialogVisible
                        }
                        recordingItems={
                            recordingActionsDialogState.recordingsButtonsList
                        }
                        onRecordingItemPress={
                            recordingActionsDialogState.onRecordingItemPress
                        }
                        onClose={
                            recordingActionsDialogState.handleCloseRecordingActionsDialog
                        }
                        recordingActionButtonPosition={
                            recordingActionsDialogState.recordingActionsButtonPosition
                        }
                    />
                )}
            </Animated.View>
        </GestureHandlerComponent>
    );
};

export default PlayerOverlay;
